import { Map, OrderedMap } from 'immutable';
import moment from 'moment';
import { createSelector } from 'reselect';

import { keepWhileEqual } from 'flow/selectors/selector-utils';
import { SortingEntry, TransferredFrom } from 'flow/types/CallRecord';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import { ContactFull } from 'flow/types/Contact';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import {
    callRecordIsFinished,
    getCallRecordsPersonalSortedByTime,
} from 'flow/utils/call-records-utils';
import { getAllCallableNumbersFromContact } from 'flow/utils/extension-utils';
import {
    getCallRecordDisplayName,
    getCallRecordDisplayTimestamp,
} from 'flow/utils/call-record-utils';
import { PageType } from 'flow/components/call-history-v2/types';
import {
    selectOwnExtensionKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';

// =========================================================================
// Selectors for the normalized call-records store. State shape:
//
//   state.callRecordsData = {
//       callRecordsUniversal: Map<CallRecordKey, CallRecord>, // canonical entities (cross-scope)
//       sortingData: {
//           extensions: Map<ExtensionKey, ScopeBucket>,
//           queues:     Map<QueueKey,     ScopeBucket>,
//       },
//   }
// =========================================================================

// -------------------------------------------------------------------------
// Canonical entity store
// -------------------------------------------------------------------------

// Private to this module — never export. The canonical map intentionally
// holds records from every scope (personal, queue, AI-coworker, solo deep
// links) so updates flow through one source of truth. Reading it directly
// in a UI selector leaks records across scopes — e.g., a queue call
// appearing in the personal history list. Lists must derive from a
// specific scope's sortingData slice; lookups must go through
// selectCallRecordFromKey.
const selectAllCallRecords = (state: State) =>
    state.callRecordsData.get('callRecordsUniversal', Map());

// -------------------------------------------------------------------------
// Per-scope sortingData
// -------------------------------------------------------------------------

// The ScopeBucket for the logged-in user's own extension: { all, byPhoneNumber }.
// One selector exists per scope type; queue and AI-agent variants live in
// queue-selectors and ai-coworker-call-records-selectors respectively.
const selectOwnCallRecordsSortingData = (state: State) =>
    state.callRecordsData.getIn(
        ['sortingData', 'extensions', selectOwnExtensionKey(state)],
        Map(),
    );

export const selectOwnCallRecordsNextSince = (state: State) =>
    selectOwnCallRecordsSortingData(state).getIn(['all', 'nextSince']);

// -------------------------------------------------------------------------
// Composite record lookups by key
// -------------------------------------------------------------------------

// Looks the record up by key. The canonical entity store is the single
// source of truth across personal, AI-coworker, queue, per-number and solo
// fetches.
export const selectCallRecordFromKey = (
    state: State,
    callRecordKey: CallRecordKey,
) => state.callRecordsData.getIn(['callRecordsUniversal', callRecordKey]);

// The party who attended-transferred the call to the viewer. Undefined for
// every call that wasn't transferred, which is most of them.
export const selectCallRecordTransferredFrom = (
    state: State,
    callRecordKey: CallRecordKey,
): TypedMap<TransferredFrom> | undefined =>
    state.callRecordsData.getIn([
        'callRecordsUniversal',
        callRecordKey,
        'transferredFrom',
    ]);

// -------------------------------------------------------------------------
// Derived per-extension lists (own extension)
// -------------------------------------------------------------------------

// Personal extension's `all` list materialized as a Map<key, CallRecord> by
// joining the scope's sorting entries with the canonical entity store.
export const selectOwnCallRecords = createSelector(
    [selectAllCallRecords, selectOwnCallRecordsSortingData],
    (canonical, bucket) => {
        const entries = bucket.getIn(['all', 'entries'], Map());
        return entries.map((_entry, key) => canonical.get(key)).filter(Boolean);
    },
);

// Personal extension's byPhoneNumber sub-lists, shaped like the legacy
// `recordsPerNumber` map so existing consumers (RelatedTabContent,
// ContactCallHistoryWidgetTW) can read it unchanged. Transitional — will be
// dropped once those consumers migrate to keys + field selectors as part of
// the prop-drilling cleanup follow-up.
export const selectOwnCallRecordsByPhoneNumber = createSelector(
    [selectAllCallRecords, selectOwnCallRecordsSortingData],
    (canonical, bucket) => {
        const byPhoneNumber = bucket.get('byPhoneNumber', Map());
        return byPhoneNumber.map((slice) => {
            const records = slice
                .get('entries', Map())
                .map((_entry, key) => canonical.get(key))
                .filter(Boolean);
            return Map({
                records,
                nextBefore: slice.get('nextBefore'),
                nextSince: slice.get('nextSince'),
            });
        });
    },
);

// Pagination cursor for paginating older records of a specific number.
// Reads straight from sortingData — no need to materialize the canonical join.
export const selectOwnCallRecordsNextBeforeForNumber = (
    state: State,
    phoneNumber: string,
) =>
    selectOwnCallRecordsSortingData(state).getIn([
        'byPhoneNumber',
        phoneNumber,
        'nextBefore',
    ]);

// Factory for a memoized selector returning all finished records the
// logged-in user has with any of a contact's callable numbers, sorted
// newest-first. Used by ContactCallHistoryWidgetTW.
//
// Returned as a factory so each component instance gets its own
// createSelector cache; that avoids cache thrash if multiple widgets
// ever subscribe with different contacts.
export const makeSelectOwnCallRecordsForContact = () =>
    createSelector(
        [
            selectOwnCallRecordsByPhoneNumber,
            (_state: State, contact: TypedMap<ContactFull> | undefined) =>
                contact,
        ],
        (byPhoneNumber, contact) => {
            if (!contact) return Map();
            const callableNumbers = getAllCallableNumbersFromContact(contact);
            if (!callableNumbers || callableNumbers.length === 0) return Map();

            const merged = callableNumbers.reduce(
                (acc, number) =>
                    acc.merge(byPhoneNumber.getIn([number, 'records'], Map())),
                Map(),
            );
            return getCallRecordsPersonalSortedByTime(
                merged.filter(callRecordIsFinished),
            );
        },
    );

export const selectOwnCallRecordsNotActive = createSelector(
    [selectOwnCallRecords],
    (records) => records.filter(callRecordIsFinished),
);

const selectOwnByPhoneNumberSortingEntries = (
    state: State,
    phoneNumber: string,
) =>
    selectOwnCallRecordsSortingData(state).getIn([
        'byPhoneNumber',
        phoneNumber,
        'entries',
    ]);

// Factory: history-only entries for one of own extension's phone numbers,
// grouped by display date (newest day first), each group's values sorted
// newest-first by display timestamp, projected to CallRecordKeys.
//
// SortingData-only — never reads the canonical entity store. Result is
// stable across record-content updates (transcription pending, voicemail
// state, contact attached/removed, etc.) and only changes when records
// appear/disappear from the per-number slice.
//
// Returns `undefined` while the per-number slice has never been fetched,
// so the consumer can distinguish "loading" from "loaded with zero records".
export const makeSelectOwnCallRecordKeysByPhoneNumberNotActiveDateGroups = () =>
    keepWhileEqual(
        createSelector([selectOwnByPhoneNumberSortingEntries], (entries) =>
            entries ? groupEntriesByDate(entries, 'personal') : undefined,
        ),
    );

export const selectOwnCallRecordsActive = createSelector(
    [selectOwnCallRecords],
    (records) => records.filterNot(callRecordIsFinished),
);

// The most recently answered incoming call in the user's own scope. Used by
// the premade-answers chat hook to populate [name] / [number] / [time]
// placeholders.
export const selectOwnLatestIncomingAnsweredCallRecord = createSelector(
    [selectOwnCallRecords],
    (records) =>
        records
            .valueSeq()
            .filter(
                (record) =>
                    record.get('direction') === 'incoming' &&
                    !!record.get('answered'),
            )
            .reduce(
                (latest, record) =>
                    !latest ||
                    new Date(record.get('answered')) >
                        new Date(latest.get('answered'))
                        ? record
                        : latest,
                null,
            ),
);

// -------------------------------------------------------------------------
// Field selectors. One selector per piece of CallRecord data so components
// subscribe only to the field they need, and re-render only when that field
// changes. Add more as components need them.
// -------------------------------------------------------------------------

export const selectCallRecordConnectedTime = (
    state: State,
    callRecordKey: CallRecordKey,
) => selectCallRecordFromKey(state, callRecordKey)?.get('connected');

export const selectCallRecordOmniTicketEntityKey = (
    state: State,
    callRecordKey: CallRecordKey,
) => selectCallRecordFromKey(state, callRecordKey)?.get('omniTicketEntityKey');

export const selectCallRecordContactKey = (
    state: State,
    callRecordKey: CallRecordKey,
) => selectCallRecordFromKey(state, callRecordKey)?.getIn(['contact', 'key']);

export const selectCallRecordContact = (
    state: State,
    callRecordKey: CallRecordKey,
) => selectCallRecordFromKey(state, callRecordKey)?.get('contact');

/**
 * A factory, not a plain selector: the fallback runs the number through
 * libphonenumber, and useAppSelector re-runs a selector on every dispatch.
 * reselect 3 caches a single entry, so one shared instance would thrash
 * between rows and memoize nothing. Each row makes its own with
 * `useMemo(makeSelectCallRecordDisplayName, [])`.
 */
export const makeSelectCallRecordDisplayName = () =>
    createSelector(
        [selectCallRecordFromKey, selectUserCountryCode],
        (callRecord, userCountryCode) =>
            getCallRecordDisplayName(callRecord, userCountryCode),
    );

export const selectCallRecordPhoneNumber = (
    state: State,
    callRecordKey: CallRecordKey,
) => selectCallRecordFromKey(state, callRecordKey)?.getIn(['number', 'number']);

export const selectCallRecordQueueKey = (
    state: State,
    callRecordKey: CallRecordKey,
) => selectCallRecordFromKey(state, callRecordKey)?.getIn(['queue', 'key']);

export const selectCallRecordVoiceMessage = (
    state: State,
    callRecordKey: CallRecordKey,
) => selectCallRecordFromKey(state, callRecordKey)?.get('voiceMessage');

export const selectCallRecordVoiceMessageUniqueId = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'voiceMessage',
        'uniqueId',
    ]);

export const selectCallRecordRecordingPlaybackUrl = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'recording',
        'playbackURL',
    ]);

export const selectCallRecordRecordingDownloadUrl = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'recording',
        'downloadURL',
    ]);

export const selectCallRecordRecordingDurationSeconds = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'recording',
        'durationSeconds',
    ]);

export const selectCallRecordRecordedCallKey = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'recordedCall',
        'key',
    ]);

export const selectCallRecordIsPersonalRecording = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'recordedCall',
        'isPersonalRecording',
    ]);

export const selectCallRecordIsTranscribable = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'recordedCall',
        'isTranscribable',
    ]);

// extendedTranscriptionInfo selectors return primitives so consumers' useEffect
// deps keep their pending→done transition detection unchanged.
export const selectHasExtendedTranscriptionInfo = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    !!selectCallRecordFromKey(state, callRecordKey)?.has(
        'extendedTranscriptionInfo',
    );

export const selectExtendedTranscriptionIsPending = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'extendedTranscriptionInfo',
        'pending',
    ]);

export const selectExtendedTranscriptionIsPersonal = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'extendedTranscriptionInfo',
        'personal',
    ]);

export const selectExtendedTranscriptionKey = (
    state: State,
    callRecordKey: CallRecordKey,
) =>
    selectCallRecordFromKey(state, callRecordKey)?.getIn([
        'extendedTranscriptionInfo',
        'extendedTranscriptionKey',
    ]);

// -------------------------------------------------------------------------
// Queue-scoped sortingData readers and date-group factories
// -------------------------------------------------------------------------

export const selectQueueAllSlice = (state: State, queueKey: QueueKey) =>
    state.callRecordsData.getIn(
        ['sortingData', 'queues', queueKey, 'all'],
        Map(),
    );

export const selectQueueByPhoneNumberSlice = (
    state: State,
    queueKey: QueueKey,
    bPartNumber: string,
) =>
    state.callRecordsData.getIn(
        ['sortingData', 'queues', queueKey, 'byPhoneNumber', bPartNumber],
        Map(),
    );

// Queue call records that are either in queue or answered and ongoing.
// Intended for one-shot reads from action thunks — not for use in components.
// If a component ever needs this, wrap it in createSelector so the derived
// Map is memoized and doesn't cause re-renders on unrelated state changes.
export const selectQueueCallRecordsActive = (
    state: State,
    queueKey: QueueKey,
) => {
    const canonical = selectAllCallRecords(state);
    return selectQueueAllSlice(state, queueKey)
        .get('entries', Map())
        .filter((entry) => !entry.get('disconnected'))
        .map((_entry, key) => canonical.get(key))
        .filter(Boolean);
};

// Whether the queue's history list would show this call record: the list renders
// entries that have a `disconnected`, so a record the API still reports as ongoing
// is present in the slice without being visible.
export const selectQueueCallRecordIsFinished = (
    state: State,
    queueKey: QueueKey,
    callRecordKey: CallRecordKey,
) =>
    !!selectQueueSortingEntries(state, queueKey)
        .get(callRecordKey)
        ?.get('disconnected');

const selectQueueSortingEntries = (state: State, queueKey: QueueKey) =>
    selectQueueAllSlice(state, queueKey).get('entries', OrderedMap());

const selectQueueByPhoneNumberSortingEntries = (
    state: State,
    queueKey: QueueKey,
    bPartNumber: string,
) =>
    selectQueueByPhoneNumberSlice(state, queueKey, bPartNumber).get(
        'entries',
        OrderedMap(),
    );

const byDisplayTimestampDescending =
    (pageType: PageType) =>
    (a: TypedMap<SortingEntry>, b: TypedMap<SortingEntry>) => {
        const aTime = getCallRecordDisplayTimestamp(a, pageType);
        const bTime = getCallRecordDisplayTimestamp(b, pageType);
        if (aTime === bTime) return 0;
        return (aTime ?? '') < (bTime ?? '') ? 1 : -1;
    };

// Shared shape used by both queue and personal history factories: history-only
// entries (filtered by disconnected), grouped by display date desc, each group
// sorted newest-first by display timestamp, values are CallRecordKeys.
// All reads are from SortingEntry fields — never touches canonical records.
const groupEntriesByDate = (
    entries: OrderedMap<CallRecordKey, TypedMap<SortingEntry>>,
    pageType: PageType,
) =>
    entries
        .filter((entry) => !!entry.get('disconnected'))
        .sort(byDisplayTimestampDescending(pageType))
        .groupBy((entry) =>
            moment(getCallRecordDisplayTimestamp(entry, pageType)).format(
                'YYYY-MM-DD',
            ),
        )
        .sortBy(
            (_value, key) => key,
            (dateA, dateB) => (dateA < dateB ? 1 : -1),
        )
        .map((group) => group.keySeq().toList());

// Date-grouped CallRecordKeys for a queue's history view — derived purely from
// sortingData entries, so the result is stable across record-content updates
// (transcription pending, voicemail read state, etc.) and only changes when
// records appear/disappear from the queue.
//
// Factory so each component instance gets its own createSelector cache; that
// avoids cache thrash if multiple queue history lists are ever rendered with
// different queueKeys at once.
//
// A fetch writes the entries again, and an ongoing call's entry changes as
// the call goes on, while the finished calls are the same. The groups then
// keep their object, so the history list does not render again.
export const makeSelectQueueCallRecordKeysNotActiveDateGroups = () =>
    keepWhileEqual(
        createSelector([selectQueueSortingEntries], (entries) =>
            groupEntriesByDate(entries, 'pbx'),
        ),
    );

// Same shape and benefits as makeSelectQueueCallRecordKeysNotActiveDateGroups,
// but scoped to the bPart sub-slice for the "Related calls" tab.
export const makeSelectQueueCallRecordKeysWithBPartNotActiveDateGroups = () =>
    keepWhileEqual(
        createSelector([selectQueueByPhoneNumberSortingEntries], (entries) =>
            groupEntriesByDate(entries, 'pbx'),
        ),
    );
